import { Navigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredPermissions?: string[];
  requireAll?: boolean;
}

export function RoleBasedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
}: RoleBasedRouteProps) {
  const {
    isAuthenticated,
    isLoading,
    sessionUnavailable,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-[var(--amy-bg)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--amy-accent)]"></div>
      </div>
    );
  }

  if (sessionUnavailable) return <SessionUnavailable />;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check single permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check multiple permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasAccess) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

// Simple protected route that only requires authentication
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, sessionUnavailable } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-[var(--amy-bg)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--amy-accent)]"></div>
      </div>
    );
  }

  if (sessionUnavailable) return <SessionUnavailable />;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function SessionUnavailable() {
  const { hydrateAdminSession, isLoading } = useAuth();
  return (
    <main className="min-h-screen flex items-center justify-center bg-[var(--amy-bg)] p-6 text-[var(--amy-text)]">
      <div className="max-w-md space-y-4" role="alert">
        <h1 className="text-xl font-semibold">Unable to check your session</h1>
        <p>
          The authentication service is temporarily unavailable. Retry to
          continue securely.
        </p>
        <Button
          disabled={isLoading}
          onClick={() => void hydrateAdminSession().catch(() => undefined)}
        >
          Retry session check
        </Button>
      </div>
    </main>
  );
}
